import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Flame, BadgePercent, CheckCircle2, Clock } from "lucide-react";
import { Layout } from "../components/Layout";
import { Seo, faqSchema } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal, MaskedLines } from "../components/motion";
import { AffiliateButton, ScoreBadge } from "../components/Affiliate";
import { VpnLogo } from "../components/VpnLogo";
import { FaqAccordion } from "../components/FaqAccordion";
import { NewsletterSignup } from "../components/NewsletterSignup";
import { CtaBanner } from "../components/CtaBanner";
import { VPNS, NORD } from "../data/vpns";

const DEALS = [
  {
    slug: "nordvpn",
    discount: "76% OFF",
    was: "$12.99",
    now: "$3.09",
    plan: "2-year plan",
    perks: ["Often includes 3 extra months free", "30-day money-back guarantee", "10 devices + Threat Protection included"],
    note: "Our #1 VPN at its lowest per-month rate — the deal we recommend to most readers.",
  },
  {
    slug: "surfshark",
    discount: "82% OFF",
    was: "$12.95",
    now: "$2.29",
    plan: "2-year plan",
    perks: ["Unlimited devices on one account", "30-day money-back guarantee", "CleanWeb ad blocking included"],
    note: "The best-value deal for families and multi-device households.",
  },
  {
    slug: "purevpn",
    discount: "82% OFF",
    was: "$10.95",
    now: "$1.99",
    plan: "2-year plan",
    perks: ["Lowest headline price of our top 5", "31-day money-back guarantee", "6,500+ servers in 88 countries"],
    note: "The cheapest credible VPN deal on this page.",
  },
  {
    slug: "veepn",
    discount: "75% OFF",
    was: "$9.99",
    now: "$2.50",
    plan: "2-year plan",
    perks: ["Unlimited devices", "30-day money-back guarantee", "Beginner-friendly apps"],
    note: "A simple budget deal for first-time VPN users.",
  },
  {
    slug: "adguard-vpn",
    discount: "75% OFF",
    was: "$9.99",
    now: "$2.49",
    plan: "2-year plan",
    perks: ["Built-in ad & tracker blocking", "Free 3GB/month tier available", "30-day money-back guarantee"],
    note: "VPN + ad blocker in one subscription — unique at this price.",
  },
];

const DEALS_FAQS = [
  {
    q: "Are these VPN deals legitimate?",
    a: "Yes. These are the providers' standard long-term plan discounts, verified by our team this month by checking each provider's checkout page. The 'was' price is the provider's monthly rate; the deal price is the effective per-month cost on the discounted long-term plan.",
  },
  {
    q: "What's the catch with 2-year VPN deals?",
    a: "You pay the full term upfront (e.g., NordVPN's 2-year plan bills around $83 at signup). The per-month price is dramatically lower, and every deal here includes a 30-day money-back guarantee, so you can get a full refund if it's not for you.",
  },
  {
    q: "Which VPN deal is the best overall?",
    a: "NordVPN's 2-year deal at $3.09/month is the one we recommend to most readers — you get the fastest, best-tested VPN at a fraction of its monthly rate, and promotions often add 3 extra months free.",
    cta: true,
  },
  {
    q: "Do prices go up on renewal?",
    a: "Usually, yes — most VPNs renew at a higher standard rate after the initial term. Set a reminder before renewal; you can often re-subscribe at a promotional rate or negotiate with support.",
  },
];

const useMonthEndCountdown = () => {
  const [left, setLeft] = useState({ d: 0, h: 0, m: 0 });
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const ms = end - now;
      setLeft({
        d: Math.floor(ms / 86400000),
        h: Math.floor((ms % 86400000) / 3600000),
        m: Math.floor((ms % 3600000) / 60000),
      });
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);
  return left;
};

const MONTH = new Date().toLocaleString("en-US", { month: "long", year: "numeric" });

const DealsPage = () => {
  const left = useMonthEndCountdown();
  const nordDeal = DEALS[0];

  return (
    <Layout>
      <Seo
        title={`Best VPN Deals ${MONTH}: Up to 82% Off (Verified)`}
        description={`Verified VPN deals for ${MONTH}: NordVPN from $3.09/mo (76% off), Surfshark from $2.29/mo and more. Every discount checked by our team this month.`}
        schema={faqSchema(DEALS_FAQS)}
      />
      <section className="grain relative overflow-hidden bg-[#0F172A]">
        <div className="hero-grid absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <p className="mb-4 inline-flex items-center gap-2 border border-[#4687FF]/40 bg-[#4687FF]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-[#7EAAFF]">
            <Flame size={13} /> Verified for {MONTH}
          </p>
          <h1 className="font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
            <MaskedLines lines={["VPN Deals, Checked", "By Humans Monthly"]} />
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">
            We verify every discount on this page at the provider's checkout each month — no inflated "was" prices,
            no expired coupons. Deal pricing is confirmed through the end of the month:
          </p>
          <div className="mt-6 flex items-center gap-3" data-testid="deals-countdown">
            <Clock size={18} className="text-[#4687FF]" />
            {[
              { v: left.d, l: "days" },
              { v: left.h, l: "hrs" },
              { v: left.m, l: "min" },
            ].map((t) => (
              <div key={t.l} className="border border-white/15 bg-white/[0.06] px-4 py-2 text-center">
                <span className="font-display text-2xl font-black text-white">{t.v}</span>
                <span className="ml-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">{t.l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Breadcrumbs crumbs={[{ label: "VPN Deals" }]} />

      {/* Featured NordVPN deal */}
      <section className="mx-auto max-w-5xl px-6 pt-10 lg:px-8">
        <Reveal>
          <div className="relative border-2 border-[#4687FF] bg-[#4687FF]/[0.04] p-8 shadow-[0_0_0_1px_#4687FF] sm:p-10" data-testid="featured-deal-nordvpn">
            <div className="absolute -top-3.5 left-8 flex items-center gap-1.5 bg-[#4687FF] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-white">
              <Flame size={12} /> Deal of the Month
            </div>
            <div className="flex flex-col gap-8 lg:flex-row lg:items-center">
              <div className="flex items-center gap-5">
                <VpnLogo vpn={NORD} size={80} />
                <ScoreBadge score={NORD.score} size="lg" />
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-baseline gap-3">
                  <h2 className="font-display text-3xl font-black tracking-tighter text-[#0F172A]">NordVPN</h2>
                  <span className="bg-[#0F172A] px-3 py-1 font-display text-sm font-black text-white">{nordDeal.discount}</span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{nordDeal.note}</p>
                <ul className="mt-4 grid gap-2 sm:grid-cols-3">
                  {nordDeal.perks.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-[13px] leading-snug text-slate-700">
                      <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-[#4687FF]" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="shrink-0 text-left lg:text-right">
                <p className="text-sm text-slate-400 line-through">{nordDeal.was}/mo</p>
                <p className="font-display text-4xl font-black tracking-tighter text-[#0F172A]">
                  {nordDeal.now}<span className="text-base font-bold text-slate-500">/mo</span>
                </p>
                <p className="text-xs font-semibold text-slate-500">{nordDeal.plan}</p>
                <div className="mt-4">
                  <AffiliateButton href={NORD.link} testId="featured-deal-cta">
                    Claim NordVPN Deal
                  </AffiliateButton>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* All deals */}
      <section className="mx-auto max-w-5xl space-y-6 px-6 py-16 lg:px-8" data-testid="deals-list">
        <Reveal className="mb-2">
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-[#0F172A]">
            All Verified Deals — {MONTH}
          </h2>
        </Reveal>
        {DEALS.slice(1).map((deal, i) => {
          const vpn = VPNS.find((v) => v.slug === deal.slug);
          return (
            <Reveal key={deal.slug} delay={i * 0.05}>
              <div
                data-testid={`deal-card-${deal.slug}`}
                className="flex flex-col gap-6 border border-slate-200 bg-white p-8 sm:flex-row sm:items-center"
              >
                <div className="flex items-center gap-4">
                  <VpnLogo vpn={vpn} size={64} />
                  <span className="border border-[#4687FF]/40 bg-[#4687FF]/10 px-2.5 py-1 font-display text-xs font-black text-[#2563eb]">
                    {deal.discount}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-xl font-extrabold tracking-tight text-[#0F172A]">{vpn.name}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{deal.note}</p>
                  <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
                    {deal.perks.map((p) => (
                      <li key={p} className="flex items-center gap-1.5 text-xs text-slate-600">
                        <BadgePercent size={13} className="shrink-0 text-[#4687FF]" /> {p}
                      </li>
                    ))}
                  </ul>
                  <Link to={`/reviews/${vpn.slug}`} className="mt-2 inline-block text-xs font-semibold text-[#4687FF] underline underline-offset-4">
                    Read our {vpn.name} review →
                  </Link>
                </div>
                <div className="shrink-0 text-left sm:text-right">
                  <p className="text-sm text-slate-400 line-through">{deal.was}/mo</p>
                  <p className="font-display text-3xl font-black tracking-tighter text-[#0F172A]">
                    {deal.now}<span className="text-sm font-bold text-slate-500">/mo</span>
                  </p>
                  <p className="text-xs font-semibold text-slate-500">{deal.plan}</p>
                  <div className="mt-3">
                    <AffiliateButton href={vpn.link} variant="dark" testId={`deal-cta-${deal.slug}`} className="!px-5 !py-2.5 !text-xs">
                      Claim Deal
                    </AffiliateButton>
                  </div>
                </div>
              </div>
            </Reveal>
          );
        })}
      </section>

      {/* How we verify */}
      <section className="border-y border-slate-200 bg-slate-50 py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-[#0F172A]">How We Verify These Deals</h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">
              At the start of every month, we go through each provider's checkout flow and record the real price of
              the longest-term plan, including any bonus months. The "was" price is the provider's own monthly rate —
              we never invent inflated comparison prices. If a promotion ends mid-month, we update this page within
              48 hours. Buying through our links may earn us a commission at no extra cost to you; it never affects
              which deals we list first — that's decided by our independent rankings.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Newsletter */}
      <section className="grain relative overflow-hidden bg-[#0F172A] py-16">
        <div className="hero-grid absolute inset-0" />
        <div className="relative mx-auto max-w-4xl px-6 lg:px-8">
          <Reveal>
            <NewsletterSignup variant="banner" />
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-6 py-20 lg:px-8">
        <Reveal className="mb-10">
          <h2 className="font-display text-3xl font-black tracking-tighter text-[#0F172A]">VPN Deal FAQs</h2>
        </Reveal>
        <Reveal>
          <FaqAccordion faqs={DEALS_FAQS} />
        </Reveal>
      </section>

      <CtaBanner
        title="The best deal is the VPN you'll actually keep."
        subtitle="NordVPN at $3.09/month is our tested #1 pick at its lowest rate — with a 30-day money-back guarantee if you change your mind."
        ctaLabel="Claim the NordVPN Deal"
      />
    </Layout>
  );
};

export default DealsPage;
